import { authConfig } from '@/services/auth/auth-config';
import { sentryService } from '@/services/telemetry/sentry';
import { fetchParallelBlob } from './fetch-parallel';

const BLOB_READ_TIMEOUT_MS = 15_000;

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    let settled = false;

    const cleanup = (): void => {
      clearTimeout(timer);
      reader.onloadend = null;
      reader.onerror = null;
    };

    const fail = (err: Error): void => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    };

    const succeed = (value: string): void => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };

    const timer = setTimeout(() => {
      reader.abort();
      fail(new Error('Blob to data URL conversion timed out'));
    }, BLOB_READ_TIMEOUT_MS);

    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        succeed(reader.result);
      } else {
        fail(new Error('Failed to convert blob to data URL: empty result'));
      }
    };
    reader.onerror = () =>
      fail(new Error('Failed to convert blob to data URL'));

    reader.readAsDataURL(blob);
  });
}

function buildAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const apiKey = authConfig.getApiKey();
  const kioskId = authConfig.getKioskId();
  if (apiKey) headers['X-Kiosk-Api-Key'] = apiKey;
  if (kioskId) headers['X-Kiosk-Id'] = kioskId;
  return headers;
}

// For a data: URL, a URL.revokeObjectURL() call is not necessary. Thus, the
// returned URL cannot cause a memory leak on a kiosk that operates 24/7.
export async function fetchAuthenticatedImage(url: string): Promise<string> {
  // Reject invalid URLs early — fetch() coerces undefined/null to "undefined"/
  // "null" and resolves them as relative URLs, which can return the SPA's
  // index.html as an "image" data URL.
  if (!url || typeof url !== 'string') {
    throw new Error(`fetchAuthenticatedImage: invalid URL: ${String(url)}`);
  }

  const startTime = performance.now();
  const trackError = (): void => {
    sentryService.trackMetric(
      'image.authenticated_fetch_error_time_ms',
      performance.now() - startTime,
      'millisecond'
    );
  };

  let blob: Blob;
  try {
    blob = await fetchParallelBlob(url, {
      headers: { ...buildAuthHeaders(), ...sentryService.getTraceHeaders() },
    });
  } catch (error) {
    trackError();
    throw error;
  }

  // Reject non-image responses — a catch-all route may return HTML with 200,
  // and blobToDataUrl would happily encode it as data:text/html;base64,...,
  // which the <img> tag cannot render.
  if (blob.type && !blob.type.startsWith('image/')) {
    throw new Error(
      `fetchAuthenticatedImage: expected image, got ${blob.type} (${blob.size} bytes) from ${url}`
    );
  }
  if (!blob.size) {
    throw new Error(
      `fetchAuthenticatedImage: empty response (0 bytes) from ${url}`
    );
  }

  sentryService.trackMetric(
    'image.authenticated_fetch_time_ms',
    performance.now() - startTime,
    'millisecond',
    {
      url_suffix: url.slice(-30),
      blob_size: blob.size,
      return_type: 'data_url',
    }
  );

  return blobToDataUrl(blob);
}
